import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { Button, IconButton, Typography } from '../../../../components/atoms';
import {
  ArrowLeft as ArrowLeftIcon,
  X as XIcon,
} from '../../../../components/icons';
import { Dialog } from '../../../../components/layouts';
import { Slide } from '../../../../components/layouts/Slide';
import { Currency } from '../../../../core/api/general/general.types';
import {
  MenuItem,
  MenuItemOptionSetItem,
  NutritionMenuItem,
} from '../../../../core/api/menu/menu.types';
import { analyticsService } from '../../../../core/services';
import {
  AddItemHookPayload,
  BasketSource,
} from '../../../../core/types/basket.types';
import {
  formatNumberToCurrency,
  getBasketItemTotal,
} from '../../../../utils/basketUtils';
import { useAllergens } from '../../hooks/useAllergens';
import { OptionSet } from '../../partials/OptionSet';
import { ProgressBtnType } from '../../types';
import { useMenuOptionsUiState } from '../../utils';
import { MenuDialogAddControls, MenuDialogItemDetails } from '..';
import { MenuItemDetails } from './components/MenuItemDetails';

type Props = {
  currency: Currency;
  isNutritionInfoError?: boolean;
  item: MenuItem;
  languageCode: string;
  menuItemNutritionInfo?: NutritionMenuItem;
  menuItemOptionSetItemNutritionInfos: NutritionMenuItem[];
  onAddItem: (payload: AddItemHookPayload) => void;
  restaurantPhoneNumber: string;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  showDialog: boolean;
  showNutritionInfo: boolean;
  translate: TranslateFunction;
};

const useStyles = makeStyles((theme: Theme) => ({
  buttons: {
    width: '100%',

    '& .buttonState': {
      width: '100%',
    },
  },

  dialog: {
    '& .MuiDialogContent-root': {
      display: 'flex',
      flexFlow: 'column nowrap',
      padding: 0,
      overflowX: 'hidden',
    },
    '& .fd-text__h2': {
      margin: 0,
    },
    '& .MuiDialogTitle-root': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,

      '& .iconLeft': {
        display: 'block',
      },
    },
  },

  dialogActions: {
    display: 'flex',
    flexFlow: 'row nowrap',
    width: '100%',
  },

  optionSets: {
    overflowX: 'hidden',
  },

  nextButtonText: {
    textAlign: 'center',
  },

  row: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
  },

  price: {
    padding: theme.spacing(0.75, 1),
    textAlign: 'right',
    width: theme.spacing(16),

    '& .fd-text__body1': {
      color: theme.palette.grey[600],
      fontSize: theme.typography.pxToRem(10),
      lineHeight: 1.2,
      textAlign: 'right',
      textTransform: 'uppercase',
    },
    '& .fd-text__h3': {
      textAlign: 'right',
    },
  },

  summaryBox: {
    backgroundColor: theme.palette.grey[200],
    flexGrow: 1,

    '& .fd-text__h3': {
      padding: theme.spacing(2.5, 2, 2),
    },
    '& .fd-text__body2': {
      padding: theme.spacing(0, 2, 1.5),
    },
  },

  summaryItem: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
  },

  [theme.breakpoints.up('sm')]: {
    dialog: {
      '& .MuiDialog-paperScrollPaper': {
        height: 'calc(100vh - 48px)', // - 48px = 24px padding top and bottom
        maxHeight: '720px',
      },

      '& .MuiDialogTitle-root': {
        '& .iconRight': {
          display: 'block',
        },
      },
    },

    summaryBox: {
      '& .fd-text__h3': {
        padding: theme.spacing(3, 3, 2),
      },
      '& .fd-text__body2': {
        padding: theme.spacing(0, 3, 1.5),
      },
    },
  },
}));

export function MenuOptionsDialog(props: Props): JSX.Element {
  const {
    currency,
    isNutritionInfoError,
    item,
    languageCode,
    menuItemNutritionInfo,
    menuItemOptionSetItemNutritionInfos,
    onAddItem,
    restaurantPhoneNumber,
    setShowDialog,
    showDialog,
    showNutritionInfo,
    translate,
  } = props;

  const theme = useTheme();
  const classes = useStyles();

  const dialogActionsStyles = clsx(
    classes.dialogActions,
    'fd-section__options-controls',
  );
  const summaryBoxStyles = clsx(
    classes.summaryBox,
    'fd-section__options-summary',
  );

  const [quantity, setQuantity] = useState(1);

  const optionSetTop = useRef(null);
  const isSmallScreen = useMediaQuery(theme?.breakpoints.down('sm'));

  const optionSets = useMemo(
    () =>
      [...item.MenuItemOptionSets].sort(
        (a, b) => a.DisplayOrder - b.DisplayOrder,
      ),
    [item.MenuItemId, item.MenuItemOptionSets],
  );

  const { state, actions } = useMenuOptionsUiState(optionSets);
  const {
    indexCurrent,
    menuOptionSetCurrent: optionSet,
    optionsDraft,
    optionsSelected,
    progressBtnEnabled,
    progressBtnType,
  } = state;
  const { goBack, goForward, resetUiState } = actions;

  const isFirstOptionSet = indexCurrent === 0;
  const isOptionsComplete = indexCurrent === optionSets?.length;
  const previousIndex = useRef(indexCurrent);

  const chosenItems = {
    Item: item,
    Options: [...optionsSelected, ...optionsDraft],
  };
  const totalConfigured = getBasketItemTotal(chosenItems) * quantity;
  const itemPrice = item.Price;
  const totalCurrency = formatNumberToCurrency(
    totalConfigured,
    currency,
    languageCode,
  );

  useEffect(() => {
    if (optionSetTop.current) {
      optionSetTop.current.scrollIntoView();
    }
    previousIndex.current = indexCurrent;
  }, [indexCurrent]);

  const { allergens } = useAllergens(
    menuItemNutritionInfo,
    menuItemOptionSetItemNutritionInfos,
  );

  if (!optionSet && !isOptionsComplete) {
    return null;
  }

  const handleGoBack = (): void => {
    goBack();
  };

  const handleGoForward = (): void => {
    goForward();
  };

  const handleCloseDialog = (): void => {
    resetUiState();
    setShowDialog(false);
  };

  const handleCancelAddItem = (): void => {
    const modifierIds = optionsSelected.map((option) =>
      option.MenuItemOptionSetItemId.toString(),
    );
    const eventData = {
      amp_itemName: item.Name,
      amp_productId: item.MenuItemId.toString(),
      amp_label: 'Item Cancelled',
      amp_addedModifierIds: modifierIds,
    };
    analyticsService.track(eventData);
    handleCloseDialog();
  };

  const handleAddItem = (count: number): void => {
    onAddItem({
      item,
      options: optionsSelected,
      quantity: count,
      source: BasketSource.MenuItemDetailDialog,
    });
    resetUiState();
    handleCloseDialog();
  };

  const renderDialogButtons = (): JSX.Element => {
    if (progressBtnType === ProgressBtnType.ADD_TO_BASKET) {
      return (
        <MenuDialogAddControls
          itemName={item.Name}
          handleAddItem={(): void => handleAddItem(quantity)}
          quantity={quantity}
          setQuantity={setQuantity}
          totalCurrency={totalCurrency}
          translate={translate}
        />
      );
    }

    if (progressBtnType === ProgressBtnType.NEXT) {
      return (
        <Button
          aria-label={translate('Next') as string}
          data-fd="options-next-button"
          disabled={!progressBtnEnabled}
          onClick={handleGoForward}
          fullWidth
        >
          <Typography className={classes.nextButtonText} variant="h3">
            {translate('Next')}
          </Typography>
        </Button>
      );
    }

    return null;
  };

  const renderStartIcon = (): JSX.Element => {
    if (isFirstOptionSet && !isSmallScreen) {
      return null;
    }

    const firstMobileScreen = isFirstOptionSet && isSmallScreen;
    return (
      <IconButton
        aria-label={translate('Back') as string}
        className="iconLeft"
        color="secondary"
        onClick={firstMobileScreen ? handleCancelAddItem : handleGoBack}
      >
        <ArrowLeftIcon />
      </IconButton>
    );
  };

  const renderPrice = (o: MenuItemOptionSetItem): JSX.Element | null => {
    if (!o.Price) {
      return null;
    }

    return (
      <Typography variant="body2">
        {formatNumberToCurrency(o.Price, currency, languageCode)}
      </Typography>
    );
  };

  const renderSelectedOptions = (): JSX.Element[] =>
    optionsSelected.map((o) => (
      <div key={o.MenuItemOptionSetItemId} className={classes.summaryItem}>
        <Typography variant="body2">{o.Name}</Typography>

        {renderPrice(o)}
      </div>
    ));

  const renderOptionSet = (): JSX.Element => {
    const direction = indexCurrent > previousIndex.current ? 'left' : 'right';
    const isSlideEnabled =
      !isFirstOptionSet ||
      (isFirstOptionSet && indexCurrent < previousIndex.current);

    return (
      <Slide
        appear={isSlideEnabled}
        direction={direction}
        in
        key={optionSet.MenuItemOptionSetId}
        mountOnEnter
        unmountOnExit
      >
        <div>
          <OptionSet
            currency={currency}
            optionSet={optionSet}
            languageCode={languageCode}
            translate={translate}
          />
        </div>
      </Slide>
    );
  };

  const renderItemDetails = (): JSX.Element => {
    if (showNutritionInfo) {
      return (
        <MenuItemDetails
          energyContent={menuItemNutritionInfo?.EnergyContent}
          isOptionsComplete={isOptionsComplete}
          isNutritionInfoError={isNutritionInfoError}
          menuItem={item}
          menuItemAllergens={allergens}
          optionSet={!isOptionsComplete ? renderOptionSet() : undefined}
          restaurantPhoneNumber={restaurantPhoneNumber}
          showImageAndName={isFirstOptionSet}
          translate={translate}
        />
      );
    }

    return (
      <>
        {isFirstOptionSet && (
          <MenuDialogItemDetails
            description={item.Description}
            imageUrl={item.ImageUrl}
            name={item.Name}
          />
        )}

        {!isOptionsComplete && (
          <div className={classes.optionSets} ref={optionSetTop}>
            {renderOptionSet()}
          </div>
        )}
      </>
    );
  };

  const dialogContent = (
    <>
      <div className={classes.itemBox}>{renderItemDetails()}</div>

      {!isFirstOptionSet && (
        <section
          aria-label={translate('Menu_OptionsDialog_Summary') as string}
          className={summaryBoxStyles}
        >
          <Typography variant="h3">
            {translate('Menu_OptionsDialog_Summary')}
          </Typography>

          <div className={classes.row}>
            <Typography variant="body2">{item.Name}</Typography>

            {itemPrice !== 0 && (
              <Typography variant="body2">
                {formatNumberToCurrency(itemPrice, currency, languageCode)}
              </Typography>
            )}
          </div>

          {renderSelectedOptions()}
        </section>
      )}
    </>
  );

  const dialogActions = (
    <div className={dialogActionsStyles}>
      <div className={classes.buttons}>{renderDialogButtons()}</div>

      {!isOptionsComplete && (
        <div className={classes.price}>
          <Typography>{translate('Total')}</Typography>
          <Typography variant="h3">
            {formatNumberToCurrency(totalConfigured, currency, languageCode)}
          </Typography>
        </div>
      )}
    </div>
  );

  const dialogEndIcon = (
    <IconButton
      aria-label={translate('Back_to_menu') as string}
      className="iconRight"
      color="secondary"
      onClick={handleCancelAddItem}
    >
      <XIcon />
    </IconButton>
  );

  return (
    <Dialog
      actions={dialogActions}
      className={classes.dialog}
      dialogName="restaurant-menu-options"
      endIcon={dialogEndIcon}
      onClose={handleCancelAddItem}
      open={showDialog}
      startIcon={renderStartIcon()}
      title={!isFirstOptionSet && item.Name}
    >
      {dialogContent}
    </Dialog>
  );
}
